import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import { colors } from "../../styles/colors";
import { down } from "../../styles/breakpoints";
import { pageShellRoot, tablePaginationResponsive } from "../../styles/pageShell";

const useStyles = makeStyles((theme: Theme) => ({
  contentMain: pageShellRoot,

  /* Lands on <main>, which already carries an inline margin-left for the
     sidebar — so no width here. See pageShell.ts. */
  poiRoot: {
    minWidth: 0,
    padding: "24px",
    backgroundColor: theme?.palette?.background?.default || colors.bgcolor,
    [down.md]: { padding: "20px 16px" },
    [down.sm]: { padding: "16px 12px" },
    ...tablePaginationResponsive,
  },

  containerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
    padding: "0 8px",
    [down.sm]: {
      flexDirection: "column",
      alignItems: "flex-start",
      marginBottom: "16px",
      padding: 0,
    },
  },

  divIconArrow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  card: {
    "&.MuiPaper-root": {
      backgroundColor: theme?.palette?.background?.paper || colors.WHITE,
      borderRadius: 16,
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
      border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
      overflow: "hidden",
    },
  },

  cardContent: {
    "&.MuiCardContent-root": {
      padding: 24,
      "&:last-child": { paddingBottom: 24 },
      [down.sm]: {
        padding: 16,
        "&:last-child": { paddingBottom: 16 },
      },
    },
  },

  /* KPI strip. `minWidth: 0` on the cards is what stops a long number from
     forcing the row to overflow instead of wrapping. */
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },

  statCard: {
    "&.MuiPaper-root": {
      minWidth: 0,
      borderRadius: 12,
      border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
      boxShadow: "none",
    },
  },

  statValue: {
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.2,
    [down.sm]: { fontSize: 22 },
  },

  statLabel: {
    fontSize: 12,
    color: theme?.palette?.text?.secondary || "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },

  tabsBar: {
    borderBottom: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    marginBottom: 24,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      minHeight: 48,
    },
  },

  /* The table needs ~900px on a single line. Rather than hide columns at
     random widths, the container scrolls horizontally on small screens and the
     page itself never overflows. */
  tableWrap: {
    "&.MuiPaper-root": {
      borderRadius: 8,
      border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
      boxShadow: "none",
      overflowX: "auto",
    },
  },

  sourceRowDisabled: {
    opacity: 0.55,
  },

  /* Trust is the single most consulted number on the sources screen, so it
     gets a monospace treatment: columns of digits line up and are scannable. */
  trustChip: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 700,
    minWidth: 44,
  },

  /* A retention ceiling is a legal constraint, not a preference. It is styled
     as a warning so it reads as "you may not keep this" at a glance. */
  retentionWarning: {
    "&.MuiChip-root": {
      backgroundColor: "#fff4e5",
      color: "#b26a00",
      fontWeight: 600,
      border: "1px solid #ffcc80",
    },
  },

  reviewCard: {
    "&.MuiPaper-root": {
      borderRadius: 12,
      border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
      boxShadow: "none",
      marginBottom: 16,
    },
  },

  reviewGrid: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: "16px",
    alignItems: "stretch",
    [down.md]: {
      gridTemplateColumns: "1fr",
    },
  },

  reviewSide: {
    padding: 16,
    borderRadius: 8,
    backgroundColor: theme?.palette?.grey?.[50] || "#fafafa",
    border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    minWidth: 0,
  },

  reviewVs: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    [down.md]: { flexDirection: "row" },
  },

  reviewActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 16,
    justifyContent: "flex-end",
    [down.sm]: { justifyContent: "stretch", "& > *": { flex: 1 } },
  },

  fieldRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    marginBottom: 8,
    minWidth: 0,
  },

  fieldValue: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  emptyState: {
    padding: "48px 24px",
    textAlign: "center",
    color: theme?.palette?.text?.secondary || "#6b7280",
  },

  /* Dropzone. The dashed border is the near-universal "drop here" affordance,
     and the whole box is clickable so the flow also works for anyone who
     never drags files. */
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    padding: "32px 16px",
    borderRadius: 12,
    border: `2px dashed ${theme?.palette?.divider || "#c7ccd1"}`,
    backgroundColor: theme?.palette?.grey?.[50] || "#fafafa",
    cursor: "pointer",
    textAlign: "center",
    transition: "border-color .15s ease, background-color .15s ease",
    "&:hover": {
      borderColor: theme?.palette?.primary?.main || "#1976d2",
    },
  },

  dropzoneActive: {
    borderColor: theme?.palette?.primary?.main || "#1976d2",
    backgroundColor: "rgba(25, 118, 210, 0.06)",
  },

  monoSmall: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
  },
}));

export default useStyles;
